import { useMemo } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router";
import { useAuthSession } from "@/features/auth/hooks/use-auth-session";
import { useDeployments } from "@/features/deployment-selection/hooks/use-deployments";
import { SelectedDeploymentProvider } from "@/features/deployment-selection/hooks/use-selected-deployment";
import { DeploymentFloorplanPreloader } from "@/features/deployment-dashboard/shared/deployment-floorplan-preloader";
import { RuntimeSessionProvider } from "@/features/runtime-session/hooks/runtime-session-provider";
import { ApiError } from "@/shared/api/api-error";
import { Callout } from "@/shared/ui/callout";
import { LoadingState } from "@/shared/ui/loading-state";

const EMPTY_DEPLOYMENTS: readonly never[] = [];

function SelectedDeploymentError({ error }: { error: unknown }) {
  if (error instanceof ApiError) {
    if (error.statusCode === 403) {
      return (
        <Callout severity="error" title="Deployment access denied" sx={{ m: 3 }}>
          You do not have permission to view deployments for this customer.
        </Callout>
      );
    }

    return (
      <Callout severity="error" title="Deployment could not be loaded" sx={{ m: 3 }}>
        {error.message}
      </Callout>
    );
  }

  return (
    <Callout severity="error" title="Deployment could not be loaded" sx={{ m: 3 }}>
      Please check your connection and try again.
    </Callout>
  );
}

export function SelectedDeploymentGuard() {
  const { deploymentId } = useParams();
  const location = useLocation();
  const authSession = useAuthSession();
  const deploymentsQuery = useDeployments();

  // Memoize derived values before any conditional returns
  const deployments = deploymentsQuery.data ?? EMPTY_DEPLOYMENTS;
  const selectedDeployment = useMemo(
    () => deployments.find((deployment) => deployment.deployment_id === deploymentId),
    [deployments, deploymentId]
  );

  const providerValue = useMemo(
    () =>
      selectedDeployment && deploymentId
        ? {
            deploymentId,
            selectedDeployment,
            deployments,
          }
        : null,
    [deploymentId, selectedDeployment, deployments]
  );

  // Early returns after hooks
  if (!deploymentId) {
    return <Navigate to="/deployments" replace />;
  }

  if (!authSession.data || deploymentsQuery.isPending || deploymentsQuery.isLoading) {
    return <LoadingState message="Loading deployment..." />;
  }

  if (deploymentsQuery.error) {
    return <SelectedDeploymentError error={deploymentsQuery.error} />;
  }

  if (!selectedDeployment || !providerValue) {
    return <Navigate to="/deployments" replace />;
  }

  const isGeneratedReportDownload = location.pathname.includes("/generated-reports/download/");

  return (
    <SelectedDeploymentProvider value={providerValue}>
      <RuntimeSessionProvider deploymentId={deploymentId} deployment={selectedDeployment}>
        {!isGeneratedReportDownload && <DeploymentFloorplanPreloader deploymentId={deploymentId} />}
        <Outlet />
      </RuntimeSessionProvider>
    </SelectedDeploymentProvider>
  );
}
